import React, { useEffect, useMemo, useState } from "react";
import RelationshipDrawer from "../RelationshipDrawer";
import CommonSelect from "@/shared/components/ui/Select/CommonSelect";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import CommonPhoneInput from "@/shared/components/ui/Input/CommonPhoneInput";
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  getPhoneNumberWithCountryCode,
  parseE164Phone,
} from "@/shared/utils/phoneCountryCodes";
import { useGetCampaigns } from "@/features/campaigns/hooks/useCampaign";
import {
  useMoveContactToCampaign,
  useRelationshipMappingDetail,
} from "../../hooks/useAccountRelationship";
import { showError } from "@/shared/utils/toast";
import Loader from "@/shared/components/ui/Loaders/Loader";
import linkedinIcon from "@/assets/svg/Linkedin-square.svg";
import { Link as LinkIcon } from "lucide-react";
import NewCampaignModal from "@/features/campaigns/components/UserCampaigns/components/NewCampaignModal";

const CampaignDrawer = ({
  isOpen,
  onClose,
  drawerType,
  relationshipMappingId,
  contactName = "this contact",
}) => {
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [email, setEmail] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState(
    DEFAULT_PHONE_COUNTRY_CODE,
  );
  const [phoneNationalNumber, setPhoneNationalNumber] = useState("");
  const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false);

  const { data: campaignsData, isLoading: isLoadingCampaigns } =
    useGetCampaigns(1, 100, "", isOpen);
  const { data: relationshipMappingDetail } = useRelationshipMappingDetail({
    relationshipMappingId,
    enabled: isOpen && !!relationshipMappingId,
  });

  const { mutate: moveContactToCampaign, isPending: isEnrolling } =
    useMoveContactToCampaign({
      action: onClose,
    });

  const campaignOptions = useMemo(
    () =>
      campaignsData?.campaigns?.map((campaign) => ({
        label: campaign?.name || campaign?.campaignName || "Untitled campaign",
        value: String(campaign?.id),
      })) ?? [],
    [campaignsData],
  );

  const enrollmentStatus = relationshipMappingDetail?.enrollment_status;
  const isAlreadyEnrolled = Boolean(enrollmentStatus?.is_enrolled);
  const selectedCampaignName = useMemo(
    () =>
      campaignOptions.find((campaign) => campaign.value === selectedCampaignId)
        ?.label ?? "",
    [campaignOptions, selectedCampaignId],
  );
  const isSelectedCampaignCurrent =
    isAlreadyEnrolled &&
    selectedCampaignId &&
    String(enrollmentStatus?.campaign_id) === String(selectedCampaignId);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedCampaignId("");
    setEmail("");
    setLinkedinUrl("");
    setPhoneCountryCode(DEFAULT_PHONE_COUNTRY_CODE);
    setPhoneNationalNumber("");
    setShowCreateCampaignModal(false);
  }, [isOpen, relationshipMappingId]);

  useEffect(() => {
    if (!isOpen || !relationshipMappingDetail) return;

    const crmContact = relationshipMappingDetail.crm_contact ?? {};
    const nextLinkedinUrl =
      crmContact.linkedin_url || relationshipMappingDetail.linkedin_url || "";
    const nextEmail = crmContact.email || "";
    const nextWhatsappNumber = crmContact.whatsapp_number || "";
    const parsedPhone = parseE164Phone(nextWhatsappNumber);

    setLinkedinUrl(nextLinkedinUrl);
    setEmail(nextEmail);
    setPhoneCountryCode(parsedPhone.countryCode);
    setPhoneNationalNumber(parsedPhone.nationalNumber);
  }, [isOpen, relationshipMappingDetail]);

  const handleSave = () => {
    if (!relationshipMappingId) {
      showError(
        "Select a contact from the list before enrolling in a campaign.",
      );
      return;
    }

    if (!selectedCampaignId) {
      showError("Please select a campaign.");
      return;
    }

    if (isAlreadyEnrolled) {
      showError(
        isSelectedCampaignCurrent
          ? "This contact is already enrolled in the selected campaign."
          : `This contact is already enrolled in ${enrollmentStatus?.campaign_name || "another campaign"}.`,
      );
      return;
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      showError("Please enter a valid email address.");
      return;
    }

    moveContactToCampaign({
      campaignId: selectedCampaignId,
      relationshipMappingId,
      email: email.trim(),
      whatsappNumber: phoneNationalNumber
        ? getPhoneNumberWithCountryCode(phoneCountryCode, phoneNationalNumber)
        : "",
      linkedinUrl: linkedinUrl.trim(),
    });
  };

  const handleCreateCampaignClose = (finalId) => {
    setShowCreateCampaignModal(false);
    if (typeof finalId === "string" && finalId) {
      setSelectedCampaignId(finalId);
    }
  };

  const isPrimaryDisabled = isEnrolling || !selectedCampaignId || isAlreadyEnrolled;

  return (
    <>
      <RelationshipDrawer
        isOpen={isOpen}
        onClose={onClose}
        drawerType={drawerType}
        title="Add to Campaigns"
        description={`Select a campaign to continue nurturing and engaging ${contactName}`}
        primaryButtonText="Enroll in Campaign"
        secondaryButtonText="Cancel"
        isPrimaryLoading={isEnrolling}
        isPrimaryDisabled={isPrimaryDisabled}
        onPrimaryClick={handleSave}
        isSecondaryDisabled={isEnrolling}
      >
        {isLoadingCampaigns && campaignOptions.length === 0 ? (
          <div className="flex justify-center py-8">
            <Loader text="Loading campaigns..." />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {isAlreadyEnrolled ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-xs font-medium text-amber-900">
                  Currently enrolled in{" "}
                  {enrollmentStatus?.campaign_name || "another campaign"}
                </p>
                <p className="mt-0.5 text-[10px] text-amber-700">
                  Remove this contact from that campaign before enrolling them
                  somewhere else.
                </p>
              </div>
            ) : selectedCampaignId ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-xs font-medium text-emerald-900">
                  Ready to enroll in {selectedCampaignName || "this campaign"}.
                </p>
              </div>
            ) : null}

            <div className="flex flex-col gap-2 w-full">
              <h5 className="text-xs font-medium text-gray-500 font-[Inter]">
                Select Campaign
              </h5>
              <CommonSelect
                options={campaignOptions}
                value={selectedCampaignId}
                onChange={(value) => setSelectedCampaignId(value ?? "")}
                onCreateNew={() => setShowCreateCampaignModal(true)}
                createNewLabel="Create new Campaign"
                placeholder="Select campaign"
                disabled={isEnrolling}
                width="w-full"
                classNames={{
                  trigger: "min-w-0 shrink-0",
                  value:
                    "!text-gray-800 font-inter !font-medium !text-[0.715rem]",
                }}
              />
            </div>

            <div className="flex flex-col gap-2 w-full">
              <h5 className="text-xs font-medium text-gray-500 font-[Inter]">
                LinkedIn URL
              </h5>
              <CommonInput
                placeholder="Enter LinkedIn URL"
                type="text"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                rightIcon={<LinkIcon size={16} className="text-gray-500" />}
                icon={
                  <img src={linkedinIcon} alt="Linkedin" className="w-4 h-4" />
                }
                inputClassName="placeholder:!text-[0.6375rem] text-[0.6375rem]"
                disabled={isEnrolling}
              />
            </div>

            <div className="flex gap-3 w-full">
              <div className="flex flex-col gap-2 w-full">
                <h5 className="text-xs font-medium text-gray-500 font-[Inter]">
                  Email
                </h5>
                <CommonInput
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-9 w-full"
                  inputClassName="!text-[0.715rem] placeholder:!text-[0.715rem] placeholder:text-gray-400"
                  type="email"
                  placeholder="Enter email"
                  disabled={isEnrolling}
                />
              </div>

              <div className="flex flex-col gap-2 w-full">
                <h5 className="text-xs font-medium text-gray-500 font-[Inter]">
                  WhatsApp Number
                </h5>
                <CommonPhoneInput
                  countryCode={phoneCountryCode}
                  phoneNumber={phoneNationalNumber}
                  onChange={({ countryCode, nationalNumber }) => {
                    setPhoneCountryCode(countryCode);
                    setPhoneNationalNumber(nationalNumber);
                  }}
                  placeholder="Enter phone number"
                  disabled={isEnrolling}
                />
              </div>
            </div>
          </div>
        )}
      </RelationshipDrawer>

      {showCreateCampaignModal && (
        <NewCampaignModal
          isOpen={showCreateCampaignModal}
          onClose={handleCreateCampaignClose}
          width="1000px"
        />
      )}
    </>
  );
};

export default CampaignDrawer;
